/**
 * Totem home shell + phase widget configuration.
 * Widget lists are the UI skeleton; travel data (and temporary widget mocks) fill them at runtime.
 * Structure copy lives in localization-app under totem-home.widgets.*.
 */

import { TOTEM_HOME_WIDGET_MOCKS, TOTEM_HOME_WIDGET_MOCK_TRANSLATIONS } from './totem-home-widgets.mock';

export type TotemHomeTourPhase = 'before'|'during'|'after'

/** Default shell/branding; later may be overridden by environment values. */
export const DEFAULT_TOTEM_CONFIG = {
    logo: null as string | null,
    name: 'Demo Tour Operator',
    title: '',
    linked: null as string | null,
    linkUrl: null as string | null,
    linkText: null as string | null,
    location: null as string | null,
    background: null as string | null,
    footerLogo: null as string | null,
    headerLogo: null as string | null,
    disableChat: null as boolean | null,
    screenSaver: null as any,
    splashScreen: null as string | null,
    whatsappChat: null as string | null,
    footerTextColor: null as string | null,
    headerTextColor: null as string | null,
    footerBackgroundColor: null as string | null,
    headerBackgroundColor: null as string | null,
    channel: null as string | null,
};

export class TotemHomeTourPhaseConfig {
    /** Widget skeleton per phase. Content comes from travel and/or widget mocks. */
    static widgets: { [phase:string]: Array<any> } = {
        '*': [{
                id: 'tour-operator-identity',
                name: 'totem-widget-identity',
                title: null,
                color: null,
                params: {
                    image: 'https://api.dev.entourance.com/uploads/images/1742997179-201623.png',
                }
            }],
        'before': [
            {
                id: 'customization-destination',
                name: 'totem-widget-destination',
                title: 'totem-home.widgets.customization-destination.title',
                color: null,
                params: {}
            }, {
                id: 'tour-travel-documents',
                name: 'totem-widget-buttons',
                title: 'totem-home.widgets.tour-travel-documents.title',
                color: null,
                params: {}
            }, {
                id: 'departure-before-leaving',
                name: 'totem-widget-checklist',
                title: 'totem-home.widgets.departure-before-leaving.title',
                color: null,
                params: {
                    editable: true
                }
            }, {
                id: 'customization-details',
                name: 'totem-widget-details',
                title: 'totem-home.widgets.customization-details.title',
                color: null,
                params: {}
            }, {
                id: 'customization-travel-insurance',
                name: 'totem-widget-products',
                title: 'totem-home.widgets.customization-travel-insurance.title',
                color: null,
                params: {
                    noFullScreen: true
                }
            }, {
                id: 'tour-viaggiare-sicuri',
                name: 'totem-widget-viaggiare-sicuri',
                title: 'totem-home.widgets.tour-viaggiare-sicuri.title',
                color: null,
                params: {
                    phase: 'before'
                }
            }
        ],
        'during': [
            {
                id: 'customization-where',
                name: 'totem-widget-destination',
                title: 'totem-home.widgets.customization-where.title',
                color: null,
                params: {}
            }, {
                id: 'customization-details',
                name: 'totem-widget-details',
                title: 'totem-home.widgets.customization-details.title',
                color: null,
                params: {}
            }, {
                id: 'during-activities',
                name: 'totem-widget-activities',
                title: 'totem-home.widgets.during-activities.title',
                color: null,
                params: {}
            }, {
                id: 'customization-challenges',
                name: 'totem-widget-challenge',
                title: 'totem-home.widgets.customization-challenges.title',
                color: null,
                params: {
                    editable: true
                }
            }, {
                id: 'tour-viaggiare-sicuri',
                name: 'totem-widget-viaggiare-sicuri',
                title: 'totem-home.widgets.tour-viaggiare-sicuri.title',
                color: null,
                params: {
                    phase: 'during'
                }
            }, {
                id: 'useful-contacts',
                name: 'totem-widget-text',
                title: 'totem-home.widgets.useful-contacts.title',
                color: null,
                params: {
                    logo: 'fa-exclamation-triangle',
                    title: 'totem-home.widgets.useful-contacts.heading',
                    description: 'totem-home.widgets.useful-contacts.description',
                    color: 'primary',
                }
            }, {
                id: 'during-day-survey',
                name: 'totem-widget-day-survey',
                title: 'totem-home.widgets.during-day-survey.title',
                color: null,
                params: {}
            }
        ],
        'after': [
            {
                id: 'after-survey-current',
                name: 'totem-widget-text',
                title: null,
                color: null,
                params: {
                    logo: 'fa-file-pen',
                    title: 'totem-home.widgets.after-survey-current.title',
                    description: 'totem-home.widgets.after-survey-current.description',
                    color: 'primary',
                }
            }, {
                id: 'after-traveller-profile',
                name: 'totem-widget-text',
                title: null,
                color: null,
                params: {
                    logo: 'https://api.dev.entourance.com/uploads/images/1768564443-traveller-profile-icon.png',
                }
            }, {
                id: 'after-survey-next',
                name: 'totem-widget-products',
                title: 'totem-home.widgets.after-survey-next.title',
                color: null,
                params: {
                    noFullScreen: true,
                    verticalList: true,
                }
            }
        ]
    }

    private static _translateWidgetRec(
        widget: any,
        language: string,
        translate: (id: string) => string | null,
    ) {
        if(widget != null && typeof widget == 'object') {
            for(let i in widget) {
                if(typeof widget[i] == 'string') {
                    const key = widget[i] as string;
                    const fromLoc = translate(key);
                    if(fromLoc != null) {
                        widget[i] = fromLoc;
                        continue;
                    }

                    const fromMock = TOTEM_HOME_WIDGET_MOCK_TRANSLATIONS[language]?.[key]
                        || TOTEM_HOME_WIDGET_MOCK_TRANSLATIONS['en_EN']?.[key];
                    if(fromMock != null) {
                        widget[i] = fromMock;
                    }
                }
                else {
                    this._translateWidgetRec(widget[i], language, translate)
                }
            }
        }
    }

    private static _applyWidgetMock(widget: any) {
        const mock = widget?.id != null ? TOTEM_HOME_WIDGET_MOCKS[widget.id] : null;
        if (!mock) return widget;

        return {
            ...widget,
            params: {
                ...(widget.params || {}),
                ...JSON.parse(JSON.stringify(mock)),
            },
        };
    }

    private static _translateWidget(
        widget: any,
        language: string,
        translate: (id: string) => string | null,
    ) {
        let _retVal = this._applyWidgetMock(JSON.parse(JSON.stringify(widget)))

        this._translateWidgetRec(_retVal, language, translate);

        return _retVal;
    }

    static getPhaseConfig(
        phase: TotemHomeTourPhase,
        language: string,
        translate: (id: string) => string | null,
    ): Array<any> {
        let _retVal = []

        for(let _item of this.widgets['*']) {
            _retVal.push(this._translateWidget(_item, language, translate))
        }

        if(this.widgets[phase] != null) {
            for(let _item of this.widgets[phase]) {
                _retVal.push(this._translateWidget(_item, language, translate))
            }
        }

        return _retVal;
    }
}
